import { LitElement, html, css } from 'lit';

export class CrusherInput extends LitElement {
  static formAssociated = true;

  static styles = css`
    :host { display: block; }
    .form-item { margin-bottom: var(--crusher-input-margin-bottom, var(--crusher-spacing-4)); }
    .label {
      display: block; font-weight: var(--crusher-font-weight-medium);
      font-size: var(--crusher-font-size-sm); color: var(--crusher-fg-muted);
      margin-bottom: var(--crusher-spacing-2);
    }
    .input {
      display: block;
      width: 100%;
      height: var(--crusher-input-height, auto);
      padding: var(--crusher-input-padding, var(--crusher-spacing-3) var(--crusher-spacing-4));
      font-size: var(--crusher-input-font-size, var(--crusher-font-size-base));
      font-family: var(--crusher-input-font-family, var(--crusher-font-family-body));
      color: var(--crusher-input-color, var(--crusher-fg));
      background: var(--crusher-input-bg, var(--crusher-surface));
      border: var(--crusher-input-border-width, var(--crusher-component-border-weight)) solid var(--crusher-input-border, var(--crusher-border));
      border-radius: var(--crusher-input-radius, var(--crusher-radius-md));
      cursor: inherit;
      transition: all var(--crusher-motion-duration-base) var(--crusher-motion-easing-inout); box-sizing: border-box;
    }
    .input:focus {
      outline: var(--crusher-input-focus-outline, var(--crusher-focus-width) solid var(--crusher-color-brand-primary));
      outline-offset: var(--crusher-input-focus-outline-offset, var(--crusher-focus-offset));
      border-color: var(--crusher-input-focus-border, transparent);
      box-shadow: var(--crusher-input-focus-shadow, none);
    }
    .input::placeholder { color: var(--crusher-fg-muted); opacity: var(--crusher-opacity-muted); }
    .input[aria-invalid="true"] { border-color: color-mix(in srgb, var(--crusher-feedback-danger) 70%, transparent); }
  `;

  static properties = {
    label: { type: String },
    type: { type: String },
    placeholder: { type: String },
    value: { type: String },
    name: { type: String, reflect: true },
    autocomplete: { type: String },
    minlength: { type: Number },
    maxlength: { type: Number },
    required: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    invalid: { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.label = 'Input';
    this.type = 'text';
    this.placeholder = '';
    this.value = '';
    this.name = '';
    this.autocomplete = '';
    this.minlength = undefined;
    this.maxlength = undefined;
    this.required = false;
    this.disabled = false;
    this.invalid = false;
    this._internals = this.attachInternals?.();
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('tabindex')) this.tabIndex = -1;
  }

  focus() { this.renderRoot?.getElementById('input')?.focus(); }

  firstUpdated() {
    this._syncFormState();
  }

  updated() {
    this._syncFormState();
  }

  _validityFlags(input) {
    return input.validity.valid ? {} : {
      valueMissing: input.validity.valueMissing,
      typeMismatch: input.validity.typeMismatch,
      patternMismatch: input.validity.patternMismatch,
      tooLong: input.validity.tooLong,
      tooShort: input.validity.tooShort,
      rangeUnderflow: input.validity.rangeUnderflow,
      rangeOverflow: input.validity.rangeOverflow,
      stepMismatch: input.validity.stepMismatch,
      badInput: input.validity.badInput,
      customError: input.validity.customError,
    };
  }

  _syncFormState() {
    const input = this.renderRoot?.getElementById('input');
    this._internals?.setFormValue(this.disabled ? null : this.value);
    if (!input || !this._internals?.setValidity) return;
    const flags = this._validityFlags(input);
    this._internals.setValidity(flags, input.validationMessage, input);
  }

  checkValidity() {
    return this._internals?.checkValidity?.() ?? this.renderRoot?.getElementById('input')?.checkValidity() ?? true;
  }

  reportValidity() {
    return this._internals?.reportValidity?.() ?? this.renderRoot?.getElementById('input')?.reportValidity() ?? true;
  }

  formResetCallback() {
    this.value = this.getAttribute('value') ?? '';
    this.invalid = false;
    this._syncFormState();
  }

  _onInput(e) {
    this.value = e.target.value;
    this.invalid = !e.target.validity.valid;
    this._syncFormState();
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  }

  render() {
    return html`
      <div class="form-item">
        ${this.label ? html`<label class="label" for="input">${this.label}${this.required ? ' *' : ''}</label>` : null}
        <input
          class="input"
          id="input"
          .type=${this.type}
          .placeholder=${this.placeholder}
          .value=${this.value}
          autocomplete=${this.autocomplete || ''}
          minlength=${this.minlength ?? ''}
          maxlength=${this.maxlength ?? ''}
          ?required=${this.required}
          ?disabled=${this.disabled}
          aria-invalid=${this.invalid ? 'true' : 'false'}
          @input=${this._onInput}
          @change=${() => this.dispatchEvent(new Event('change', { bubbles: true, composed: true }))}
          />
      </div>
    `;
  }
}
customElements.define('crusher-input', CrusherInput);
